import { Fill, Stroke, Style, Circle as CircleStyle, RegularShape } from 'ol/style';

/**
 * Symbology mapper for FGB layers from backend API
 * Maps layer descriptions to appropriate visual styles
 */

// Layer type detection patterns - Exact matches from API
const LAYER_PATTERNS = {
  // Buildings & Structures
  buildings: /^building footprint$/i,

  // Administrative
  boundaries: /^ward boundary$/i,
  projectArea: /^project area$/i,

  // Water Infrastructure (Blue tones)
  drainage: /^drainage$/i,
  waterBodies: /^water bodies$/i,
  waterValve: /^water valve$/i,
  chambers: /^chambers$/i,
  manholes: /^manholes$/i,
  outfalls: /^outfalls$/i,

  // Roads & Paths (Gray/Black tones)
  roads: /^road network$/i,
  footpath: /^footpath$/i,
  stairs: /^stairs$/i,

  // Electric Infrastructure (Yellow/Orange tones)
  electricPoles: /^electric_poles$/i,
  electricJunction: /^electrict junction box$/i,
  transformer: /^transformer$/i,

  // Lighting (Bright Yellow tones)
  lampPoles: /^lamp poles$/i,
  solarLamp: /^solar lamp$/i,

  // Communication (Purple tones)
  cameraPole: /^camera pole$/i,
  telephoneBox: /^telephone box$/i,
  wifiPole: /^wifi pole$/i,

  // Traffic & Transport (Red/Orange tones)
  trafficLight: /^traffic light$/i,
  evCharging: /^ev charging station$/i,
  petrolPump: /^petrol pump$/i,

  // Landmarks & Reference Points (Distinct colors)
  landmarks: /^land mark$/i,
  tbmLocation: /^tbm location$/i,
  pscpLocation: /^pscp location$/i,

  // Vegetation (Green tones)
  trees: /^trees$/i,
};

/**
 * Detect layer category from description
 */
const detectCategory = (description) => {
  if (!description) return 'default';

  const desc = description.toLowerCase();

  for (const [category, pattern] of Object.entries(LAYER_PATTERNS)) {
    if (pattern.test(desc)) {
      return category;
    }
  }

  return 'default';
};

/**
 * Get symbology style for a layer based on its description
 * @param {Object} layer - Layer object from API with description field
 * @returns {Style} OpenLayers Style object
 */
export const getLayerSymbology = (layer) => {
  const description = layer?.description || '';
  const category = detectCategory(description);


  return SYMBOLOGY_STYLES[category] || SYMBOLOGY_STYLES.default;
};

/**
 * Predefined symbology styles for each category
 */
const SYMBOLOGY_STYLES = {
  // === BUILDINGS & STRUCTURES === (Orange)
  buildings: new Style({
    fill: new Fill({
      color: 'rgba(249, 115, 22, 0.4)', // Orange
    }),
    stroke: new Stroke({
      color: '#f97316',
      width: 1,
    }),
  }),

  // === ADMINISTRATIVE === (Red - USGS Standard for boundaries)
  boundaries: new Style({
    fill: new Fill({
      color: 'rgba(220, 38, 38, 0.05)',
    }),
    stroke: new Stroke({
      color: '#dc2626',
      width: 3,
      lineDash: [10, 5],
    }),
  }),

  projectArea: new Style({
    fill: new Fill({
      color: 'rgba(239, 68, 68, 0.1)',
    }),
    stroke: new Stroke({
      color: '#ef4444',
      width: 2,
      lineDash: [8, 4],
    }),
  }),

  // === WATER INFRASTRUCTURE === (Blue - USGS Standard, APWA Blue for water)
  drainage: new Style({
    stroke: new Stroke({
      color: '#0284c7', // Blue - APWA standard for water
      width: 3,
    }),
  }),

  waterBodies: new Style({
    fill: new Fill({
      color: 'rgba(59, 130, 246, 0.4)', // Standard water blue
    }),
    stroke: new Stroke({
      color: '#1d4ed8',
      width: 1,
    }),
  }),

  waterValve: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: '#3b82f6' }), // Blue - water infrastructure
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  chambers: new Style({
    image: new RegularShape({
      points: 4,
      radius: 5,
      fill: new Fill({ color: '#22c55e' }), // Green - sewer/drain (APWA standard)
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  manholes: new Style({
    image: new CircleStyle({
      radius: 4,
      fill: new Fill({ color: '#22c55e' }), // Green - sewer infrastructure (APWA standard)
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  outfalls: new Style({
    image: new RegularShape({
      points: 3,
      radius: 6,
      fill: new Fill({ color: '#22c55e' }), // Green - drainage (APWA standard)
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === ROADS & PATHS === (Black - USGS Standard)
  roads: new Style({
    stroke: new Stroke({
      color: '#000000', // Black - standard for roads
      width: 4,
    }),
  }),

  footpath: new Style({
    stroke: new Stroke({
      color: '#4b5563', // Dark gray
      width: 2,
      lineDash: [5, 3],
    }),
  }),

  stairs: new Style({
    stroke: new Stroke({
      color: '#374151', // Dark gray
      width: 2,
      lineDash: [3, 3],
    }),
  }),

  // === ELECTRIC INFRASTRUCTURE === (Red - APWA Standard for electric power)
  electricPoles: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: '#dc2626' }), // Red - APWA electric standard
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  electricJunction: new Style({
    image: new RegularShape({
      points: 4,
      radius: 5,
      angle: Math.PI / 4,
      fill: new Fill({ color: '#ef4444' }), // Red - electric
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  transformer: new Style({
    image: new RegularShape({
      points: 4,
      radius: 6,
      fill: new Fill({ color: '#b91c1c' }), // Dark red - electric
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === LIGHTING === (Red - part of electric infrastructure per APWA)
  lampPoles: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: '#f87171' }), // Light red - lighting cables
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  solarLamp: new Style({
    image: new CircleStyle({
      radius: 5,
      fill: new Fill({ color: '#fca5a5' }), // Lighter red - solar lighting
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === COMMUNICATION === (Orange - APWA Standard for telecom)
  cameraPole: new Style({
    image: new RegularShape({
      points: 3,
      radius: 6,
      fill: new Fill({ color: '#f97316' }), // Orange - telecom
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  telephoneBox: new Style({
    image: new RegularShape({
      points: 4,
      radius: 5,
      fill: new Fill({ color: '#ea580c' }), // Orange - telecom
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  wifiPole: new Style({
    image: new RegularShape({
      points: 3,
      radius: 6,
      rotation: Math.PI,
      fill: new Fill({ color: '#fb923c' }), // Light orange - telecom
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === TRAFFIC & TRANSPORT ===
  trafficLight: new Style({
    image: new RegularShape({
      points: 4,
      radius: 6,
      angle: Math.PI / 4,
      fill: new Fill({ color: '#000000' }), // Black - infrastructure
      stroke: new Stroke({ color: '#fbbf24', width: 2 }), // Yellow outline
    }),
  }),

  evCharging: new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#dc2626' }), // Red - electric power
      stroke: new Stroke({ color: '#22c55e', width: 2 }), // Green outline for eco
    }),
  }),

  petrolPump: new Style({
    image: new RegularShape({
      points: 4,
      radius: 6,
      fill: new Fill({ color: '#eab308' }), // Yellow - gas/petroleum
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === LANDMARKS & REFERENCE === (Black with distinct outlines)
  landmarks: new Style({
    image: new RegularShape({
      points: 5,
      radius: 8,
      radius2: 3,
      fill: new Fill({ color: '#1f2937' }), // Black
      stroke: new Stroke({ color: '#fbbf24', width: 2 }), // Yellow outline
    }),
  }),

  tbmLocation: new Style({
    image: new RegularShape({
      points: 4,
      radius: 7,
      angle: Math.PI / 4,
      fill: new Fill({ color: '#7c3aed' }), // Purple - special reference
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  pscpLocation: new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#3b82f6' }), // Blue - special location
      stroke: new Stroke({ color: '#ffffff', width: 2 }),
    }),
  }),

  // === VEGETATION === (Green - USGS Standard)
  trees: new Style({
    image: new CircleStyle({
      radius: 6,
      fill: new Fill({ color: '#16a34a' }), // Standard vegetation green
      stroke: new Stroke({ color: '#14532d', width: 2 }),
    }),
  }),

  // === DEFAULT ===
  default: new Style({
    fill: new Fill({
      color: 'rgba(156, 163, 175, 0.4)',
    }),
    stroke: new Stroke({
      color: '#6b7280',
      width: 1,
    }),
    image: new CircleStyle({
      radius: 4,
      fill: new Fill({ color: '#9ca3af' }),
      stroke: new Stroke({ color: '#ffffff', width: 1 }),
    }),
  }),
};

/**
 * Get legend color for a layer
 */
export const getLegendColor = (layer) => {
  const category = detectCategory(layer?.description || '');

  const colorMap = {
    // Buildings & Structures (Orange)
    buildings: '#f97316',

    // Administrative (Red)
    boundaries: '#dc2626',
    projectArea: '#ef4444',

    // Water Infrastructure (Blue)
    drainage: '#0284c7',
    waterBodies: '#3b82f6',
    waterValve: '#3b82f6',
    chambers: '#22c55e', // Green for sewer/drain
    manholes: '#22c55e',
    outfalls: '#22c55e',

    // Roads & Paths (Black/Gray)
    roads: '#000000',
    footpath: '#4b5563',
    stairs: '#374151',

    // Electric Infrastructure (Red - APWA standard)
    electricPoles: '#dc2626',
    electricJunction: '#ef4444',
    transformer: '#b91c1c',

    // Lighting (Light Red)
    lampPoles: '#f87171',
    solarLamp: '#fca5a5',

    // Communication (Orange - APWA telecom)
    cameraPole: '#f97316',
    telephoneBox: '#ea580c',
    wifiPole: '#fb923c',

    // Traffic & Transport
    trafficLight: '#000000',
    evCharging: '#dc2626',
    petrolPump: '#eab308',

    // Landmarks (Black with yellow outline)
    landmarks: '#1f2937',
    tbmLocation: '#7c3aed',
    pscpLocation: '#3b82f6',

    // Vegetation (Green)
    trees: '#16a34a',

    // Default
    default: '#9ca3af',
  };

  return colorMap[category] || colorMap.default;
};

/**
 * Fetch layers from API and assign symbology
 */
export const fetchLayersWithSymbology = async () => {
  try {
    const response = await fetch('https://backend.geonetra.tech/api/v1/fgb?offset=0&limit=100');
    const result = await response.json();

    if (result.data && Array.isArray(result.data)) {
      // Map each layer with its symbology
      return result.data.map(layer => ({
        ...layer,
        symbology: getLayerSymbology(layer),
        legendColor: getLegendColor(layer),
        category: detectCategory(layer.description),
      }));
    }

    return [];
  } catch (error) {
    return [];
  }
};

/**
 * Get symbology details for sidebar legend
 * Returns fill color, stroke color, and geometry type
 */
export const getSymbologyDetails = (layer) => {
  const category = detectCategory(layer?.description || '');
  const style = SYMBOLOGY_STYLES[category] || SYMBOLOGY_STYLES.default;

  let fillColor = null;
  let strokeColor = null;
  let geometryType = 'point'; // default

  // Extract fill color
  const fill = style.getFill && style.getFill();
  if (fill) {
    fillColor = fill.getColor();
    geometryType = 'polygon';
  }

  // Extract stroke color
  const stroke = style.getStroke && style.getStroke();
  if (stroke) {
    strokeColor = stroke.getColor();
    if (!fillColor) {
      geometryType = 'line';
    }
  }

  // Check for point symbology (image)
  const image = style.getImage && style.getImage();
  if (image) {
    geometryType = 'point';
    const imageFill = image.getFill && image.getFill();
    const imageStroke = image.getStroke && image.getStroke();

    if (imageFill) {
      fillColor = imageFill.getColor();
    }
    if (imageStroke) {
      strokeColor = imageStroke.getColor();
    }
  }

  return {
    fillColor,
    strokeColor,
    geometryType,
    category,
  };
};

export default {
  getLayerSymbology,
  getLegendColor,
  fetchLayersWithSymbology,
  detectCategory,
  getSymbologyDetails,
};
